import { useState, useEffect } from "react";
import { Receipt, Clock, Users, Truck, FileText, Mail, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatINR, formatDate } from "../../lib/api";
import { DriveDocuments } from "../DriveDocuments";

export function History({ lr, data, navigate, docTypes }) {
  const [checks, setChecks] = useState(null);
  useEffect(() => {
    if (lr?.id) api.get(`/lrs/${lr.id}/doc-checks`).then((r) => setChecks(r.data)).catch(() => {});
  }, [lr?.id]);
  const showChecks = checks && ((checks.ewbs || []).length > 0 || checks.delivery_mismatch || checks.gstin_check);
  return (
    <>
      {showChecks && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-doc-checks">
          <div className="section-title flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Document Checks</div>
          {(checks.ewbs || []).map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm py-1" data-testid={`ewb-check-${e.id}`}>
              {e.status === "expired" ? <AlertTriangle className="w-4 h-4 text-[#8A2526] shrink-0" />
                : e.status === "expiring" ? <AlertTriangle className="w-4 h-4 text-[#B25E00] shrink-0" />
                : e.status === "valid" ? <CheckCircle2 className="w-4 h-4 text-[#2F6F4E] shrink-0" />
                : <FileText className="w-4 h-4 text-[#A09E96] shrink-0" />}
              <span className={e.status === "expired" ? "text-[#8A2526] font-medium" : e.status === "expiring" ? "text-[#B25E00]" : "text-[#4A4842]"}>
                {e.status === "expired" ? `E-way bill ${e.number} EXPIRED` : e.status === "expiring" ? `E-way bill ${e.number} expiring` : e.status === "valid" ? `E-way bill ${e.number} valid` : `E-way bill ${e.number || e.name} — expiry not detected`}
                {e.valid_until ? ` (till ${formatDate(e.valid_until)})` : ""}
              </span>
            </div>
          ))}
          {checks.delivery_mismatch && (
            <div className="flex items-start gap-2 text-sm py-1 text-[#B25E00]" data-testid="delivery-mismatch">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Delivery address differs across documents: {checks.delivery_addresses.join("  |  ")}</span>
            </div>
          )}
          {checks.gstin_check && (
            <div className={`flex items-start gap-2 text-sm py-1 ${checks.gstin_check.matched ? "text-[#2F6F4E]" : "text-[#B25E00]"}`} data-testid="gstin-check">
              {checks.gstin_check.matched ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
              <span>Delivery GSTIN {checks.gstin_check.gstin} ({checks.gstin_check.state}) {checks.gstin_check.matched ? "matches the delivery address." : "does NOT match the delivery address — please verify."}</span>
            </div>
          )}
        </div>
      )}
      <div className="bg-white border border-[#E5E3DB] p-4 mb-6">
        <div className="section-title flex items-center gap-2"><Receipt className="w-4 h-4" /> Linked Invoice</div>
        {data.invoice ? (
          <button className="text-[#C84B31] hover:underline font-medium" onClick={() => navigate(`/invoices?focus=${data.invoice.id}`)} data-testid="lr-detail-invoice">
            {data.invoice.invoice_number} — {formatINR(data.invoice.total)} ({data.invoice.status})
          </button>
        ) : <div className="text-sm text-[#A09E96]">Not yet invoiced.</div>}
      </div>

      <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-emails">
        <div className="section-title flex items-center gap-2"><Mail className="w-4 h-4" /> Linked Emails</div>
        {(data.emails || []).length === 0 ? <div className="text-sm text-[#A09E96]">No emails linked. Link a mail from the Inbox to mark work started.</div> : (
          <div className="divide-y divide-[#F0EDE5]">
            {data.emails.map((e) => (
              <button key={e.id} onClick={() => navigate(`/inbox?open=${e.id}`)} className="w-full text-left py-2 hover:bg-[#FAF9F5] px-1 -mx-1 rounded" data-testid={`lr-detail-email-${e.id}`}>
                <div className="text-sm font-medium text-[#1A1A18] truncate">{e.subject || "(no subject)"}</div>
                <div className="text-xs text-[#68665E]">{e.sender}{e.date_raw ? ` · ${e.date_raw}` : ""}</div>
                {e.ai_summary && <div className="text-xs text-[#68665E] mt-0.5 line-clamp-1">{e.ai_summary}</div>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E3DB] p-4 mb-6">
        <div className="section-title flex items-center gap-2"><Clock className="w-4 h-4" /> Related Expenses</div>
        {(data.expenses || []).length === 0 ? <div className="text-sm text-[#A09E96]">No expenses linked.</div> : (
          <div className="divide-y divide-[#E5E3DB]">
            {data.expenses.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2 text-sm" data-testid={`lr-detail-expense-${e.id}`}>
                <span>{formatDate(e.expense_date)} · {e.category} {e.reason ? `· ${e.reason}` : ""}</span>
                <span className="font-medium">{formatINR(e.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(lr.contacts && lr.contacts.length > 0) && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-contacts">
          <div className="section-title flex items-center gap-2"><Users className="w-4 h-4" /> People &amp; Contacts</div>
          <div className="divide-y divide-[#F0EDE5]">
            {lr.contacts.filter((c) => c.role || c.name || c.phone || c.email).map((c, i) => (
              <div key={`${c.role || ""}-${c.name || ""}-${c.phone || c.email || i}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 py-2 text-sm" data-testid={`lr-detail-contact-${i}`}>
                <span className="font-medium text-[#C84B31] w-32">{c.role || "Contact"}</span>
                <span>{c.name || "—"}</span>
                {c.phone && <a href={`tel:${c.phone}`} className="text-[#68665E] hover:underline">{c.phone}</a>}
                {c.email && <a href={`mailto:${c.email}`} className="text-[#68665E] hover:underline">{c.email}</a>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(lr.custom_fields && lr.custom_fields.length > 0) && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-custom-fields">
          <div className="section-title">Custom Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {lr.custom_fields.filter((f) => f.title || f.value).map((f, i) => (
              <div key={f.title || `cf-${i}`} className="flex justify-between border-b border-[#F0EDE5] py-1 text-sm" data-testid={`lr-detail-cf-${i}`}>
                <span className="text-[#68665E]">{f.title}</span>
                <span className="font-medium text-[#1A1A18]">{f.value || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {lr.sub_transporter && lr.sub_transporter.name && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-sub-transporter">
          <div className="section-title flex items-center gap-2"><Truck className="w-4 h-4" /> Sub-Transporter <span className="text-[11px] font-normal text-[#68665E]">(internal — not on PDF)</span></div>
          <div className="text-sm flex flex-wrap gap-x-8 gap-y-1">
            <span><span className="text-[#68665E]">Name:</span> <span className="font-medium">{lr.sub_transporter.name}</span></span>
            {lr.sub_transporter.hub_name && <span><span className="text-[#68665E]">Hub:</span> {lr.sub_transporter.hub_name}</span>}
            {lr.sub_transporter.poc_name && <span><span className="text-[#68665E]">POC:</span> {lr.sub_transporter.poc_name}{lr.sub_transporter.poc_phone ? ` · ${lr.sub_transporter.poc_phone}` : ""}</span>}
            {lr.sub_transporter.notes && <span className="text-[#68665E]">{lr.sub_transporter.notes}</span>}
          </div>
        </div>
      )}

      {((lr.documents && lr.documents.filter((d) => d.number).length > 0) || (lr.ewaybills && lr.ewaybills.filter((e) => e.number).length > 0)) && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-print-docs">
          <div className="section-title">Documents &amp; E-Way Bills <span className="text-[11px] font-normal text-[#68665E]">(printed on LR)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            {(lr.documents || []).filter((d) => d.number).map((d, i) => (
              <div key={`pdoc-${i}`} className="flex justify-between border-b border-[#F0EDE5] py-1" data-testid={`lr-detail-document-${i}`}>
                <span className="text-[#68665E]">Document No.</span>
                <span className="font-medium text-[#1A1A18]">{d.number}{d.date ? ` · ${formatDate(d.date)}` : ""}</span>
              </div>
            ))}
            {(lr.ewaybills || []).filter((e) => e.number).map((e, i) => (
              <div key={`pewb-${i}`} className="flex justify-between border-b border-[#F0EDE5] py-1" data-testid={`lr-detail-ewaybill-${i}`}>
                <span className="text-[#68665E]">E-Way Bill</span>
                <span className="font-medium text-[#1A1A18] font-mono">{e.number}{e.date ? ` · ${formatDate(e.date)}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(lr.company_docs && lr.company_docs.filter((d) => d.number).length > 0) && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-company-docs">
          <div className="section-title">Company Documents <span className="text-[11px] font-normal text-[#68665E]">(internal — not on PDF)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {lr.company_docs.filter((d) => d.number).map((d, i) => (
              <div key={d.title || `doc-${i}`} className="flex justify-between border-b border-[#F0EDE5] py-1 text-sm" data-testid={`lr-detail-doc-${i}`}>
                <span className="text-[#68665E]">{d.title}</span>
                <span className="font-medium text-[#1A1A18]">{d.number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E3DB] p-4">
        <div className="section-title flex items-center gap-2"><FileText className="w-4 h-4" /> Documents (Google Drive)</div>
        <DriveDocuments category="lr" refId={lr.id} refLabel={lr.lr_number} docTypes={docTypes} />
      </div>
    </>
  );
}
